import React from 'react';
import { 
  DoorOpen, 
  ChefHat, 
  Bed, 
  Bath, 
  Flame, 
  ArrowUpRight, 
  Droplets, 
  CloudRain,
  Compass,
  Home
} from 'lucide-react';
import { answersToGridItems, roomColors, getDirectionRotation } from '../../utils/gridMapping';

// Icon component mapping
const iconComponents = {
  entrance: DoorOpen,
  kitchen: ChefHat,
  bedroom: Bed,
  bathroom: Bath,
  puja: Flame,
  staircase: ArrowUpRight,
  undergroundTank: Droplets,
  overheadTank: CloudRain,
};

// Direction labels for the grid border
const directionLabels = {
  top: 'N',
  topRight: 'NE',
  right: 'E',
  bottomRight: 'SE',
  bottom: 'S',
  bottomLeft: 'SW',
  left: 'W',
  topLeft: 'NW',
};

/**
 * FloorPlanPreview - A live 2D preview of the house based on questionnaire answers
 * 
 * This component is purely driven by props (no internal state).
 * It uses the shared gridMapping utility for coordinate conversion.
 * 
 * @param {Object} props
 * @param {Object} props.answers - The questionnaire answers state
 * @param {boolean} props.compact - Whether to render in compact mode (mobile)
 */
const FloorPlanPreview = ({ answers, compact = false }) => {
  // Convert answers to grid items using shared mapping
  const gridItems = answersToGridItems(answers);
  
  // Create a 4x4 grid
  const gridSize = 4;
  const grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(null));
  
  // Place items on grid (handle overlaps by stacking)
  const cellContents = {};
  gridItems.forEach(item => {
    const key = `${item.row}-${item.col}`;
    if (!cellContents[key]) {
      cellContents[key] = [];
    }
    cellContents[key].push(item);
  });

  // Get facing direction for compass
  const facingDirection = answers.facingDirection;
  const hasDirection = facingDirection && facingDirection !== 'unsure';

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 border-[var(--accent-blue-200)] ${compact ? 'p-3' : 'p-4'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Home className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-[#232323]`} />
          <h3 className={`font-bold text-[#232323] ${compact ? 'text-sm' : 'text-base'}`}>
            Your Floor Plan
          </h3>
        </div>
        {hasDirection && (
          <div className="flex items-center gap-1 text-xs text-[#353535]">
            <Compass className="w-4 h-4" style={{ transform: `rotate(${getDirectionRotation(facingDirection)}deg)` }} />
            <span className="capitalize">{facingDirection}-facing</span>
          </div>
        )}
      </div>

      {/* Grid Container with Direction Labels */}
      <div className="relative">
        {/* Direction Labels */}
        <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs font-bold text-[#F59E0B]">
          {directionLabels.top}
        </div>
        <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-xs font-bold text-[#353535]">
          {directionLabels.bottom}
        </div>
        <div className="absolute top-1/2 -left-5 transform -translate-y-1/2 text-xs font-bold text-[#353535]">
          {directionLabels.left}
        </div>
        <div className="absolute top-1/2 -right-5 transform -translate-y-1/2 text-xs font-bold text-[#353535]">
          {directionLabels.right}
        </div>
        
        {/* Corner Labels */}
        <div className="absolute -top-4 -left-4 text-[10px] font-semibold text-[#9CA3AF]">
          {directionLabels.topLeft}
        </div>
        <div className="absolute -top-4 -right-4 text-[10px] font-semibold text-[#9CA3AF]">
          {directionLabels.topRight}
        </div>
        <div className="absolute -bottom-4 -left-4 text-[10px] font-semibold text-[#9CA3AF]">
          {directionLabels.bottomLeft}
        </div>
        <div className="absolute -bottom-4 -right-4 text-[10px] font-semibold text-[#9CA3AF]">
          {directionLabels.bottomRight}
        </div>

        {/* The Grid */}
        <div 
          className={`grid grid-cols-4 gap-1 mx-6 my-4 ${compact ? 'w-36 h-36' : 'w-48 h-48'} mx-auto`}
          style={{ aspectRatio: '1/1' }}
        >
          {grid.map((row, rowIndex) => 
            row.map((_, colIndex) => {
              const key = `${rowIndex}-${colIndex}`;
              const items = cellContents[key] || [];
              const isCenter = (rowIndex === 1 || rowIndex === 2) && (colIndex === 1 || colIndex === 2);
              
              return (
                <div
                  key={key}
                  className={`
                    relative border rounded-sm transition-all duration-300
                    ${isCenter ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'}
                    ${items.length > 0 ? 'border-2' : ''}
                  `}
                  style={{
                    borderColor: items.length > 0 ? roomColors[items[0].type] : undefined,
                    backgroundColor: items.length > 0 ? `${roomColors[items[0].type]}15` : undefined,
                  }}
                >
                  {items.length > 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-0.5">
                      {items.slice(0, 1).map((item, idx) => {
                        const IconComponent = iconComponents[item.type];
                        return (
                          <div key={idx} className="flex flex-col items-center">
                            {IconComponent && (
                              <IconComponent 
                                className={`${compact ? 'w-3 h-3' : 'w-4 h-4'}`}
                                style={{ color: roomColors[item.type] }}
                              />
                            )}
                            <span 
                              className={`${compact ? 'text-[6px]' : 'text-[8px]'} font-semibold leading-tight text-center`}
                              style={{ color: roomColors[item.type] }}
                            >
                              {item.label}
                            </span>
                          </div>
                        );
                      })}
                      {items.length > 1 && (
                        <span className="text-[6px] text-gray-500">+{items.length - 1}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      {gridItems.length > 0 && (
        <div className={`mt-4 pt-3 border-t border-gray-200 ${compact ? 'hidden' : 'block'}`}>
          <p className="text-xs text-[#353535] mb-2 font-semibold">Elements placed:</p>
          <div className="flex flex-wrap gap-2">
            {[...new Set(gridItems.map(item => item.type))].map(type => {
              const IconComponent = iconComponents[type];
              const labels = {
                entrance: 'Entrance',
                kitchen: 'Kitchen',
                bedroom: 'Bedroom',
                bathroom: 'Bathroom',
                puja: 'Puja Room',
                staircase: 'Staircase',
                undergroundTank: 'Underground Tank',
                overheadTank: 'Overhead Tank',
              };
              return (
                <div 
                  key={type} 
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                  style={{ backgroundColor: `${roomColors[type]}20`, color: roomColors[type] }}
                >
                  {IconComponent && <IconComponent className="w-3 h-3" />}
                  <span className="font-medium">{labels[type]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state hint */}
      {gridItems.length === 0 && (
        <div className={`text-center ${compact ? 'mt-2' : 'mt-4'}`}>
          <p className={`text-[#9CA3AF] ${compact ? 'text-[10px]' : 'text-xs'}`}>
            Answer questions to see your floor plan
          </p>
        </div>
      )}
    </div>
  );
};

export default FloorPlanPreview;
